import {Banner, Button, Link} from '@primer/react'
import type {NavigationUrls} from '../types/navigation-urls-types'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {toggleGenericFeaturePath} from '@github-ui/paths'
import type {FilesRoutePayload} from '../routes/route-payload-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export type LargeDiffOptimizationsBannerProps = {
  /**
   * Repository information including owner and name
   */
  repository: FilesRoutePayload['repository']
  /**
   * Navigation URLs for the pull request, used for the "switch back" link
   */
  urls: NavigationUrls
  /**
   * Whether the diff entries are virtualized
   */
  isDiffVirtualizationEnabled: boolean
  /**
   * Whether the PR is in single file mode
   */
  isInSingleFileMode: boolean
}
export function LargeDiffOptimizationsBanner({
  repository,
  urls,
  isDiffVirtualizationEnabled,
  isInSingleFileMode,
}: LargeDiffOptimizationsBannerProps) {
  if (!isDiffVirtualizationEnabled && !isInSingleFileMode) return null

  const isFilesVirtualizationOptInAllowed = isFeatureEnabled('pull_request_files_virtualization')
  const showVirtualizationFeaturePreviewButton =
    isInSingleFileMode && isFilesVirtualizationOptInAllowed && !isDiffVirtualizationEnabled

  async function handleFeaturePreviewToggle() {
    const formData = new FormData()
    formData.append('feature_name', 'pull_request_files_virtualization')

    await verifiedFetch(toggleGenericFeaturePath({repo: repository}), {
      body: formData,
      method: 'POST',
    })
  }

  const VIRTUALIZATION_MESSAGE = 'This page has been optimized for large pull requests.'

  if (isDiffVirtualizationEnabled)
    return (
      <Banner
        title={VIRTUALIZATION_MESSAGE}
        hideTitle
        variant="info"
        description={`${VIRTUALIZATION_MESSAGE} Some browser features (like Find on Page or Select All) may not work as expected.`}
        className="pr-mb-3"
        primaryAction={
          <Button
            variant="invisible"
            as="a"
            href={`${urls.changes}?mode=single`}
            rel="noopener noreferrer"
            onClick={handleFeaturePreviewToggle}
            className="fgColor-accent"
          >
            Switch to single file mode
          </Button>
        }
      />
    )

  if (isInSingleFileMode) {
    const SINGLE_FILE_MODE_MESSAGE = 'Due to the size of this pull request, only one file can be shown at a time.'
    const description = (
      <>
        {SINGLE_FILE_MODE_MESSAGE}{' '}
        {showVirtualizationFeaturePreviewButton && (
          <>
            <span>To view all files in a single page, </span>
            <Link
              inline
              href={`${urls.changes}?mode=virtualization`}
              onClick={handleFeaturePreviewToggle}
              rel="noreferrer"
            >
              try a new experimental mode
            </Link>
            .
          </>
        )}
      </>
    )

    return (
      <Banner
        title={'Single file mode'}
        description={description}
        hideTitle
        variant="info"
        className="pr-mb-3"
        primaryAction={
          !showVirtualizationFeaturePreviewButton && (
            <Button
              variant="invisible"
              as="a"
              href={`${urls.files}?new_files_changed=false`}
              rel="noopener noreferrer"
              onClick={handleFeaturePreviewToggle}
              className="fgColor-accent"
              target="_blank"
            >
              View in the classic experience
            </Button>
          )
        }
      />
    )
  }
}
